/**
 * @file calendarMeetings.ts
 * @brief Which calendar events are meetings, and who is in them (desktop app).
 *
 * @details
 * The web copy of the phone's `FRONTEND/src/features/calendar/calendarMeetings.ts`
 * (DES 06-10-2026: calendar sync before launch). Same rules, so the Mac and
 * the phone list the same meetings:
 * - a meeting is a timed event (not all-day), not cancelled, not declined by
 *   the user, that has not ended;
 * - rooms and resources are not people, and the user is already the host, so
 *   neither goes on the roster; the organizer counts (Exchange often leaves
 *   them out of the attendee list);
 * - the same meeting synced through two accounts shows once;
 * - it looks like a meeting: someone else is invited, or it carries a
 *   video-call link (DES 14-10-2026: a bill reminder showed as a meeting);
 * - calendars switched off in "Choose calendars" are skipped.
 * Pure: nothing here talks to the network.
 *
 * Related: ./useDesktopCalendar.ts, ./CalendarMeetingsPanel.tsx
 *
 * @author DES
 * @date 06-10-2026
 */

import type {
  DeviceCalendarEvent,
  DeviceCalendarParticipant,
} from "../../shared/desktop/desktopShell.ts";

export type CalendarAttendee = { name: string; email: string };

export type CalendarMeeting = {
  id: string;
  title: string;
  startMs: number;
  endMs: number;
  attendees: CalendarAttendee[];
};

/** What the setup page needs to pre-fill a new boardroom. */
export type CalendarMeetingPrefill = {
  title: string;
  attendees: CalendarAttendee[];
};

/** "ali.khan@acme.com" → "Ali Khan", for an attendee the calendar left unnamed. */
export function nameFromEmail(email: string): string {
  const local = (email.split("@")[0] || email).replace(/[._-]+/g, " ").trim();
  if (!local) return email;
  return local
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function isMe(p: DeviceCalendarParticipant, selfEmail: string): boolean {
  if (p.isCurrentUser) return true;
  return !!selfEmail && (p.email || "").toLowerCase() === selfEmail;
}

export function attendeesOf(e: DeviceCalendarEvent, selfEmail: string): CalendarAttendee[] {
  const seen = new Set<string>();
  const out: CalendarAttendee[] = [];
  const all = [...(e.organizer ? [e.organizer] : []), ...(e.attendees || [])];
  for (const p of all) {
    if (p.type === "room" || p.type === "resource") continue;
    if (isMe(p, selfEmail)) continue;
    const email = (p.email || "").trim().toLowerCase();
    const name = (p.name || "").trim();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    const cleanName = name && name.toLowerCase() !== email ? name : "";
    out.push({ name: cleanName || nameFromEmail(email), email });
  }
  return out;
}

/** Video-call hosts. A plain web address (a bank, a dentist) does not count. */
const MEETING_LINK =
  /\b(?:[\w-]+\.)?(?:zoom\.us|zoomgov\.com|teams\.microsoft\.com|teams\.live\.com|meet\.google\.com|webex\.com|gotomeet(?:ing)?\.(?:com|me)|whereby\.com|chime\.aws|meet\.jit\.si|join\.skype\.com|facetime\.apple\.com)\//i;

export function hasMeetingLink(e: DeviceCalendarEvent): boolean {
  return [e.location, e.url, e.notes].some((t) => !!t && MEETING_LINK.test(t));
}

/** Someone else is invited, or there is a call link. */
export function isMeetingLike(e: DeviceCalendarEvent, selfEmail: string): boolean {
  return attendeesOf(e, selfEmail).length > 0 || hasMeetingLink(e);
}

function declinedByMe(e: DeviceCalendarEvent, selfEmail: string): boolean {
  if (e.selfDeclined) return true;
  return (e.attendees || []).some((p) => isMe(p, selfEmail) && p.status === "declined");
}

export function toCalendarMeetings(
  events: DeviceCalendarEvent[],
  {
    nowMs = Date.now(),
    selfEmail = "",
    hiddenCalendarIds,
  }: { nowMs?: number; selfEmail?: string; hiddenCalendarIds?: ReadonlySet<string> } = {},
): CalendarMeeting[] {
  const me = selfEmail.trim().toLowerCase();
  const seen = new Set<string>();
  const out: CalendarMeeting[] = [];
  for (const e of events) {
    if (e.allDay || e.cancelled) continue;
    if (!(e.endMs > nowMs)) continue;
    if (declinedByMe(e, me)) continue;
    if (e.calendarId && hiddenCalendarIds?.has(e.calendarId)) continue;
    if (!isMeetingLike(e, me)) continue;
    const key = `${(e.title || "").trim().toLowerCase()}|${e.startMs}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id: e.id,
      title: (e.title || "").trim() || "Meeting",
      startMs: e.startMs,
      endMs: e.endMs,
      attendees: attendeesOf(e, me),
    });
  }
  return out.sort((a, b) => a.startMs - b.startMs);
}

/** Today 00:00 → end of tomorrow, local time. */
export function todayAndTomorrowWindow(nowMs = Date.now()): { startMs: number; endMs: number } {
  const start = new Date(nowMs);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 2);
  return { startMs: start.getTime(), endMs: end.getTime() };
}

/** Same title + same start minute as a Verblizr scheduled meeting: that one
 *  is already under Upcoming (we email its invite, so it is in the calendar
 *  too), and its Record would start a second, separate boardroom. */
export function withoutScheduled(
  meetings: CalendarMeeting[],
  scheduled: { title?: string | null; scheduled_at: string }[],
): CalendarMeeting[] {
  if (!scheduled.length) return meetings;
  const keys = new Set(
    scheduled.map(
      (m) => `${(m.title || "").trim().toLowerCase()}|${Math.round(Date.parse(m.scheduled_at) / 60000)}`,
    ),
  );
  return meetings.filter(
    (m) => !keys.has(`${m.title.trim().toLowerCase()}|${Math.round(m.startMs / 60000)}`),
  );
}
